/**
 * A completude da janela de risco (#606), calculada a partir do formulário enquanto a pessoa
 * preenche. É a mesma regra do servidor — `backend/src/services/completude.ts`, que devolve o
 * número da listagem —, com os mesmos itens, rótulos e ordem: uma mudança lá pede a mesma aqui.
 *
 * Além do que o servidor diz, cada item sabe em que seção da janela está e qual campo receber o
 * foco quando a pessoa clica nele em "Faltam".
 */

import type { Completude } from '../../services/efetivosService';

export type SecaoDaJanela = 'exposicao' | 'medidas' | 'medicoes' | 'exames' | 'documentos';

export interface CriterioDaJanela {
  rotulo: string;
  preenchido: boolean;
  secao: SecaoDaJanela;
  /** O `id` do elemento que recebe a rolagem e o foco. */
  alvo: string;
}

export interface MedicaoDoFormulario {
  measuredAt: Date | null;
  value: number | null;
  measurementEquipmentId: number | null;
  unit: string;
  technique: string;
  appliedToleranceLimit: string;
  /** Em risco com fórmula o limite nasce deste % (texto do campo); o texto do limite o servidor grava. */
  quartzPercent?: string;
  assessmentType: string;
  unhealthyFraming: boolean;
  unhealthyDegreeApplied: string;
  hazardousFraming: boolean;
  hazardousAnnex: string;
}

export interface EntradaDaCompletude {
  exposureLevel: string | null;
  startDate: Date | null;
  activityProcess: string;
  riskProbability: number | null;
  riskSeverity: number | null;
  /** Sem tipo de matriz, probabilidade e severidade nem aparecem: "Faltam" leva ao tipo. */
  riskMatrixType?: string | null;
  existingAdministrativeMeasures: string[];
  existingEngineeringMeasures: string[];
  existingAdministrativeOther: string;
  existingEngineeringOther: string;
  /** Os EPIs marcados, com o C.A. (`Epis.metodo`) e a eficácia do cadastro. */
  episEmUso: { ca: string | null | undefined; eficacia: string | null | undefined }[];
  riscoTemEpiNoCatalogo: boolean;
  avaliacaoNoRisco: string | null | undefined;
  /** O risco calcula o limite por fórmula (sílica livre, #459): o quartzo basta como limite aplicado. */
  riscoComFormula?: boolean;
  insalubre: boolean;
  perigoso: boolean;
  medicoes: MedicaoDoFormulario[];
}

/** Os ids dos campos da janela que "Faltam" sabe focar. */
export const ALVO = {
  nivel: 'risco-nivel',
  inicio: 'risco-inicio',
  atividade: 'risco-atividade',
  matriz: 'risco-matriz',
  tipoDaMatriz: 'risco-matriz-tipo',
  probabilidade: 'risco-matriz-probabilidade',
  severidade: 'risco-matriz-severidade',
  medidas: 'risco-medidas',
  epis: 'risco-epis',
  adicionarMedicao: 'risco-adicionar-medicao',
} as const;

export const alvoDaMedicao = (indice: number, campo: string): string => `medicao-${indice}-${campo}`;

const temTexto = (valor: string | null | undefined): boolean => typeof valor === 'string' && valor.trim() !== '';
const temNumero = (valor: number | null | undefined): boolean => valor !== null && valor !== undefined && !Number.isNaN(valor);

/** Risco com fórmula: o quartzo basta, porque o servidor calcula e grava o texto do limite. */
function limiteAplicadoPreenchido(entrada: EntradaDaCompletude, medicao: MedicaoDoFormulario | undefined): boolean {
  if (temTexto(medicao?.appliedToleranceLimit)) return true;
  return Boolean(entrada.riscoComFormula) && temTexto(medicao?.quartzPercent) && !Number.isNaN(Number(medicao?.quartzPercent));
}

/** A natureza da avaliação: a da medição, depois a do risco; sem marcação, decide o valor medido. */
export function naturezaDaAvaliacao(
  naMedicao: string | null | undefined,
  noRisco: string | null | undefined,
  temValorMedido: boolean,
): 'QUALITATIVE' | 'QUANTITATIVE' {
  for (const marcada of [naMedicao, noRisco]) {
    if (marcada === 'QUALITATIVE' || marcada === 'QUANTITATIVE') return marcada;
  }
  return temValorMedido ? 'QUANTITATIVE' : 'QUALITATIVE';
}

/** O índice da medição de data mais recente; empate fica com a última da lista. */
export function indiceDaMedicaoMaisRecente(medicoes: readonly { measuredAt: Date | null }[]): number {
  let indice = -1;
  medicoes.forEach((medicao, i) => {
    const atual = indice < 0 ? -Infinity : medicoes[indice]!.measuredAt?.getTime() ?? -Infinity;
    if ((medicao.measuredAt?.getTime() ?? -Infinity) >= atual) indice = i;
  });
  return indice;
}

export function criteriosDoRisco(entrada: EntradaDaCompletude): CriterioDaJanela[] {
  const indice = indiceDaMedicaoMaisRecente(entrada.medicoes);
  const medicao = indice >= 0 ? entrada.medicoes[indice] : undefined;
  const naMedicao = (campo: string): string => (medicao ? alvoDaMedicao(indice, campo) : ALVO.adicionarMedicao);
  const quantitativo =
    naturezaDaAvaliacao(medicao?.assessmentType, entrada.avaliacaoNoRisco, temNumero(medicao?.value)) === 'QUANTITATIVE';
  const epis = entrada.episEmUso;
  const criterios: CriterioDaJanela[] = [];

  if (quantitativo) {
    criterios.push(
      { rotulo: 'Medição com valor', preenchido: temNumero(medicao?.value), secao: 'medicoes', alvo: naMedicao('value') },
      {
        rotulo: 'Aparelho da medição',
        preenchido: temNumero(medicao?.measurementEquipmentId),
        secao: 'medicoes',
        alvo: naMedicao('equipment'),
      },
      { rotulo: 'Unidade da medição', preenchido: temTexto(medicao?.unit), secao: 'medicoes', alvo: naMedicao('unit') },
      { rotulo: 'Técnica de avaliação', preenchido: temTexto(medicao?.technique), secao: 'medicoes', alvo: naMedicao('technique') },
      {
        rotulo: 'Limite aplicado',
        preenchido: limiteAplicadoPreenchido(entrada, medicao),
        secao: 'medicoes',
        alvo: naMedicao('limit'),
      },
    );
  }
  if (entrada.riscoTemEpiNoCatalogo) {
    criterios.push(
      { rotulo: 'EPI em uso', preenchido: epis.length > 0, secao: 'medidas', alvo: ALVO.epis },
      {
        rotulo: 'C.A. do EPI',
        preenchido: epis.length > 0 && epis.every((epi) => temTexto(epi.ca)),
        secao: 'medidas',
        alvo: ALVO.epis,
      },
      {
        rotulo: 'Eficácia do EPI',
        preenchido: epis.length > 0 && epis.every((epi) => temTexto(epi.eficacia)),
        secao: 'medidas',
        alvo: ALVO.epis,
      },
    );
  }
  if (entrada.insalubre) {
    criterios.push({
      rotulo: 'Grau de insalubridade',
      preenchido: medicao?.unhealthyFraming === true && temTexto(medicao.unhealthyDegreeApplied),
      secao: 'medicoes',
      alvo: medicao ? 'risco-enquadramento' : ALVO.adicionarMedicao,
    });
  }
  if (entrada.perigoso) {
    criterios.push({
      rotulo: 'Anexo da NR-16',
      preenchido: medicao?.hazardousFraming === true && temTexto(medicao.hazardousAnnex),
      secao: 'medicoes',
      alvo: medicao ? 'risco-enquadramento' : ALVO.adicionarMedicao,
    });
  }
  const naMatriz = (campo: string): string => (temTexto(entrada.riskMatrixType) ? campo : ALVO.tipoDaMatriz);
  criterios.push(
    { rotulo: 'Nível de exposição', preenchido: temTexto(entrada.exposureLevel), secao: 'exposicao', alvo: ALVO.nivel },
    { rotulo: 'Data de início', preenchido: entrada.startDate !== null, secao: 'exposicao', alvo: ALVO.inicio },
    { rotulo: 'Atividade / processo', preenchido: temTexto(entrada.activityProcess), secao: 'exposicao', alvo: ALVO.atividade },
    { rotulo: 'Probabilidade da matriz', preenchido: temNumero(entrada.riskProbability), secao: 'exposicao', alvo: naMatriz(ALVO.probabilidade) },
    { rotulo: 'Severidade da matriz', preenchido: temNumero(entrada.riskSeverity), secao: 'exposicao', alvo: naMatriz(ALVO.severidade) },
    {
      rotulo: 'Medida de controle existente',
      preenchido:
        entrada.existingAdministrativeMeasures.length > 0 ||
        entrada.existingEngineeringMeasures.length > 0 ||
        temTexto(entrada.existingAdministrativeOther) ||
        temTexto(entrada.existingEngineeringOther),
      secao: 'medidas',
      alvo: ALVO.medidas,
    },
  );
  return criterios;
}

export function completudeDe(criterios: readonly CriterioDaJanela[]): Completude {
  return {
    preenchidas: criterios.filter((c) => c.preenchido).length,
    total: criterios.length,
    faltam: criterios.filter((c) => !c.preenchido).map((c) => c.rotulo),
  };
}
